import { useMemo, useState, type ComponentType } from 'react'
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDate } from '@/lib/utils'
import { useProjectContext } from '@/lib/context/ProjectContext'
import {
  EvidenceDocument,
  EvidenceReportType,
  EvidenceOutputFormat,
  REPORT_TYPE_DESCRIPTIONS,
  artifactMatchesFormat,
  findBestArtifact,
  getArtifactFormat,
  isActiveStatus,
  normalizeStatus,
  reportTypeLabel,
  useDownloadEvidenceArtifact,
  useEvidenceReports,
  useGenerateEvidenceReport,
} from '@/features/reports/api/useEvidenceReports'

const REPORT_TYPE_OPTIONS: Array<{
  value: EvidenceReportType
  label: string
  defaultFormat: EvidenceOutputFormat
  category: string
}> = [
  { value: 'requirements_doc', label: 'Generated Requirements', defaultFormat: 'pdf', category: 'requirements' },
  { value: 'traceability_matrix', label: 'Traceability Matrix', defaultFormat: 'xlsx', category: 'traceability' },
  { value: 'test_plan', label: 'Test Plan', defaultFormat: 'pdf', category: 'testing' },
  { value: 'test_execution_report', label: 'Test Execution Report', defaultFormat: 'pdf', category: 'testing' },
  { value: 'coverage_report', label: 'Coverage Report', defaultFormat: 'xlsx', category: 'testing' },
  { value: 'release_assessment', label: 'Release Readiness', defaultFormat: 'pdf', category: 'risk' },
  { value: 'risk_decision_report', label: 'Risk Decision Report', defaultFormat: 'pdf', category: 'risk' },
  { value: 'compliance_report', label: 'Compliance Report', defaultFormat: 'pdf', category: 'compliance' },
  { value: 'security_audit', label: 'Security Evidence Report', defaultFormat: 'pdf', category: 'compliance' },
  { value: 'executive_summary', label: 'Executive Summary', defaultFormat: 'pdf', category: 'executive' },
  { value: 'audit_binder', label: 'Audit Binder', defaultFormat: 'pdf', category: 'audit' },
]

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Reports' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'traceability', label: 'Traceability' },
  { value: 'testing', label: 'Testing' },
  { value: 'risk', label: 'Risk & Release' },
  { value: 'compliance', label: 'Compliance & Security' },
  { value: 'executive', label: 'Executive' },
  { value: 'audit', label: 'Audit' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'rendering', label: 'Rendering' },
  { value: 'uploading', label: 'Uploading' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
]

function getReportCategory(documentType?: string | null): string {
  const found = REPORT_TYPE_OPTIONS.find(option => option.value === documentType)
  return found?.category || 'custom'
}

function getDefaultFormat(documentType: EvidenceReportType): EvidenceOutputFormat {
  return REPORT_TYPE_OPTIONS.find(option => option.value === documentType)?.defaultFormat || 'pdf'
}

function statusVariant(status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  const normalized = normalizeStatus(status)
  if (normalized === 'completed') return 'success'
  if (normalized === 'failed' || normalized === 'cancelled') return 'destructive'
  if (normalized === 'queued') return 'secondary'
  if (normalized === 'processing' || normalized === 'rendering' || normalized === 'uploading') return 'warning'
  return 'outline'
}

function statusIcon(status?: string | null) {
  const normalized = normalizeStatus(status)
  if (normalized === 'completed') return <CheckCircle2 className="h-4 w-4" />
  if (normalized === 'failed' || normalized === 'cancelled') return <XCircle className="h-4 w-4" />
  if (normalized === 'queued') return <Clock className="h-4 w-4" />
  if (normalized === 'processing' || normalized === 'rendering' || normalized === 'uploading') {
    return <Loader2 className="h-4 w-4 animate-spin" />
  }
  return <AlertCircle className="h-4 w-4" />
}

function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`
}

function getGeneratedAt(report: EvidenceDocument): string {
  return report.completed_at || report.updated_at || report.created_at
}

function getPrimaryArtifact(report: EvidenceDocument) {
  return findBestArtifact(report.artifacts)
}

function ReportStatusBadge({ status }: { status?: string | null }) {
  return (
    <Badge variant={statusVariant(status)} className="gap-1 capitalize">
      {statusIcon(status)}
      {normalizeStatus(status) || 'unknown'}
    </Badge>
  )
}

function ArtifactBadges({ report }: { report: EvidenceDocument }) {
  const artifacts = report.artifacts || []
  const requestedFormats = report.requested_formats || []
  const formats = artifacts.length
    ? Array.from(new Set(artifacts.map(getArtifactFormat).filter(Boolean)))
    : requestedFormats.map(format => String(format).toLowerCase())

  if (!formats.length) {
    return <span className="text-xs text-muted-foreground">No artifacts yet</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {formats.map(format => (
        <Badge key={format} variant="outline" className="uppercase">
          {format}
        </Badge>
      ))}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  subtext,
  icon: Icon,
}: {
  label: string
  value: string | number
  subtext: string
  icon: ComponentType<{ className?: string }>
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="mt-1 text-2xl font-bold">{value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{subtext}</p>
          </div>
          <div className="rounded-lg bg-primary/10 p-2 text-primary">
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function GenerateReportDialog({ projectId, projectName }: { projectId: number; projectName: string }) {
  const [open, setOpen] = useState(false)
  const [reportType, setReportType] = useState<EvidenceReportType>('traceability_matrix')
  const [format, setFormat] = useState<EvidenceOutputFormat>('xlsx')
  const generateMutation = useGenerateEvidenceReport()

  const selectedType = REPORT_TYPE_OPTIONS.find(option => option.value === reportType)

  const handleReportTypeChange = (value: string) => {
    const nextType = value as EvidenceReportType
    setReportType(nextType)
    setFormat(getDefaultFormat(nextType))
  }

  const handleGenerate = async () => {
    try {
      await generateMutation.mutateAsync({
        project_id: projectId,
        project_name: projectName,
        document_type: reportType,
        requested_formats: [format],
        input_params: {
          metadata: {
            project_name: projectName,
            report_type: reportType,
            report_type_label: reportTypeLabel(reportType),
          },
        },
      })
      toast.success(`${reportTypeLabel(reportType)} generation queued`)
      setOpen(false)
    } catch (error) {
      toast.error('Unable to queue report generation')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Sparkles className="h-4 w-4" />
          Generate Report
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generate Governed Evidence Report</DialogTitle>
          <DialogDescription>
            Create a tenant-scoped report artifact from canonical project evidence. The report will be processed asynchronously and archived when complete.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={handleReportTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select report type" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {REPORT_TYPE_DESCRIPTIONS[reportType] || selectedType?.label}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Output Format</Label>
            <Select value={format} onValueChange={(value) => setFormat(value as EvidenceOutputFormat)}>
              <SelectTrigger>
                <SelectValue placeholder="Select output format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
                <SelectItem value="docx">DOCX</SelectItem>
                <SelectItem value="markdown">Markdown</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <div className="font-medium">Archive naming preview</div>
            <p className="mt-1 text-muted-foreground">
              {projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'project'}-{String(reportType).replace(/_/g, '-')}-YYYY-MM-DD-HHMMutc-v1-0.{String(format).toLowerCase()}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={generateMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
            {generateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Queue Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function ReportsPage() {
  const { selectedProject } = useProjectContext()
  const [category, setCategory] = useState('all')
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [activeView, setActiveView] = useState('all')
  const downloadMutation = useDownloadEvidenceArtifact()
  const quickGenerateMutation = useGenerateEvidenceReport()

  const projectId = selectedProject?.id
  const reportsQuery = useEvidenceReports(projectId ? { project_id: projectId, limit: 100, offset: 0 } : undefined)

  const reports = reportsQuery.data || []

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase()
    return reports.filter(report => {
      const reportCategory = getReportCategory(report.document_type)
      const normalizedStatus = normalizeStatus(report.status)
      const matchesCategory = category === 'all' || reportCategory === category
      const matchesStatus = status === 'all' || normalizedStatus === status
      const matchesView = activeView === 'all' || reportCategory === activeView
      const matchesSearch = !q || [
        report.title,
        reportTypeLabel(report.document_type),
        report.document_type,
        report.error_message || '',
      ].some(value => String(value || '').toLowerCase().includes(q))

      return matchesCategory && matchesStatus && matchesView && matchesSearch
    })
  }, [reports, search, category, status, activeView])

  const completedCount = reports.filter(report => normalizeStatus(report.status) === 'completed').length
  const activeCount = reports.filter(report => isActiveStatus(report.status)).length
  const failedCount = reports.filter(report => normalizeStatus(report.status) === 'failed').length
  const latestReport = [...reports].sort((a, b) => new Date(getGeneratedAt(b)).getTime() - new Date(getGeneratedAt(a)).getTime())[0]
  const completionRate = reports.length ? Math.round((completedCount / reports.length) * 100) : 0

  const downloadReport = async (report: EvidenceDocument, preferredFormat?: string) => {
    const artifact = findBestArtifact(report.artifacts, preferredFormat)
    try {
      await downloadMutation.mutateAsync({
        documentId: report.id,
        artifactId: artifact?.id,
        preferredFormat,
      })
    } catch (error) {
      toast.error('Unable to download report artifact')
    }
  }

  if (!selectedProject || !projectId) {
    return null
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold tracking-tight">Governed Evidence Reports</h2>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Generate, monitor, retrieve, and archive customer-facing evidence artifacts for {selectedProject.name}. Reports are generated asynchronously from canonical evidence and stored as tenant-scoped artifacts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => reportsQuery.refetch()} disabled={reportsQuery.isFetching}>
            <RefreshCw className={`h-4 w-4 ${reportsQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <GenerateReportDialog projectId={projectId} projectName={selectedProject.name} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Total Reports" value={reports.length} subtext="Project-scoped artifacts" icon={Archive} />
        <SummaryCard label="Completed" value={completedCount} subtext={`${completionRate}% completion rate`} icon={CheckCircle2} />
        <SummaryCard label="In Progress" value={activeCount} subtext="Queued or actively generating" icon={Loader2} />
        <SummaryCard label="Failed" value={failedCount} subtext="Requires review or retry" icon={AlertCircle} />
      </div>

      {activeCount > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">Report generation is in progress</div>
                <div className="text-sm text-muted-foreground">This page will automatically refresh while reports are queued, rendering, or uploading.</div>
              </div>
              <Badge variant="warning" className="gap-1">
                <Loader2 className="h-4 w-4 animate-spin" />
                {activeCount} active
              </Badge>
            </div>
            <Progress value={Math.max(10, completionRate)} className="mt-4 h-2" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Report Families</CardTitle>
          <CardDescription>
            Filter by governed evidence family. Each family follows the same lifecycle: generate, monitor, archive, retrieve.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList className="grid h-auto w-full grid-cols-2 md:grid-cols-4 xl:grid-cols-8">
              {CATEGORY_OPTIONS.map(option => (
                <TabsTrigger key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>Report Artifact Browser</CardTitle>
              <CardDescription>
                Browse generated artifacts, download prior versions, and monitor asynchronous report jobs.
              </CardDescription>
            </div>
            {latestReport && (
              <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                Latest: <span className="font-medium text-foreground">{formatDate(getGeneratedAt(latestReport))}</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search reports by title, type, or error message..."
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[190px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Artifacts</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-28 text-center text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading governed report artifacts...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Archive className="h-8 w-8" />
                        <div className="font-medium text-foreground">No reports found</div>
                        <div className="text-sm">Generate a governed evidence report to create the first artifact for this project.</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map(report => {
                    const primaryArtifact = getPrimaryArtifact(report)
                    const canDownload = normalizeStatus(report.status) === 'completed'
                    const hasXlsx = (report.artifacts || []).some(artifact => artifactMatchesFormat(artifact, 'xlsx'))
                    const hasPdf = (report.artifacts || []).some(artifact => artifactMatchesFormat(artifact, 'pdf'))

                    return (
                      <TableRow key={report.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium leading-tight">{report.title}</div>
                            <div className="text-xs text-muted-foreground">Document ID {report.id}</div>
                            {report.error_message && normalizeStatus(report.status) === 'failed' && (
                              <div className="max-w-xl text-xs text-destructive">{report.error_message}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant="outline">{reportTypeLabel(report.document_type)}</Badge>
                            <div className="text-xs text-muted-foreground capitalize">{getReportCategory(report.document_type)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ReportStatusBadge status={report.status} />
                        </TableCell>
                        <TableCell>
                          <ArtifactBadges report={report} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(getGeneratedAt(report))}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatBytes(primaryArtifact?.file_size)}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            {hasPdf && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!canDownload || downloadMutation.isPending}
                                onClick={() => downloadReport(report, 'pdf')}
                              >
                                <FileText className="h-4 w-4" />
                                PDF
                              </Button>
                            )}
                            {hasXlsx && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!canDownload || downloadMutation.isPending}
                                onClick={() => downloadReport(report, 'xlsx')}
                              >
                                <FileSpreadsheet className="h-4 w-4" />
                                XLSX
                              </Button>
                            )}
                            {!hasPdf && !hasXlsx && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!canDownload || downloadMutation.isPending}
                                onClick={() => downloadReport(report)}
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" disabled>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Traceability Matrix</CardTitle>
            <CardDescription>Generate an XLSX matrix linking requirements, evidence, verification methods, and risk.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              variant="outline"
              onClick={async () => {
                try {
                  await quickGenerateMutation.mutateAsync({
                    project_id: projectId,
                    project_name: selectedProject.name,
                    document_type: 'traceability_matrix',
                    requested_formats: ['xlsx'],
                  })
                  toast.success('Traceability Matrix generation queued')
                } catch {
                  toast.error('Unable to queue Traceability Matrix')
                }
              }}
              disabled={quickGenerateMutation.isPending}
            >
              {quickGenerateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Generate XLSX
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              Creates a timestamped, project-scoped Traceability Matrix artifact.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Evidence Lifecycle</CardTitle>
            <CardDescription>All artifacts follow the governed evidence pipeline.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <div>Canonical evidence → governed formalization → artifact rendering</div>
            <div>Artifact archive → metadata persistence → signed URL retrieval</div>
            <div>Future: evidence snapshot IDs, sidecar manifests, and report diffing</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Customer Access</CardTitle>
            <CardDescription>Completed reports are accessible without regeneration.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <div>Customers can list prior artifacts by project and report family.</div>
            <div>Downloads are served through middleware-controlled signed URLs.</div>
            <div>Tenant isolation remains enforced by metadata and archive path.</div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
